import { ref, shallowRef } from 'vue'
import { currentSession } from '@/api/tokens'
import { getResolvedSellerEntries, type ResolvedSellerEntries } from '@/api/sellerEntry'
import { sellerEntryVisible as pathVisible, type SellerEntryModule } from './sellerEntryScope'

const resolved = shallowRef<ResolvedSellerEntries | null>(null)
export const sellerEntryState = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
let owner = '', generation = 0
let expiry: ReturnType<typeof setTimeout> | null = null
const ownerNow = () => {
  const s = currentSession()
  return s?.subjectType === 'TENANT' ? JSON.stringify([s.subjectType, s.tenantId, s.userId, s.subjectId]) : ''
}
const fresh = () => owner !== '' && owner === ownerNow() && sellerEntryState.value === 'ready'
  && !!resolved.value && Date.parse(resolved.value.expiresAt) > Date.now()
export function sellerModuleVisible(module: SellerEntryModule): boolean {
  return fresh() && !!resolved.value?.visibleModules.includes(module)
}
export function sellerEntryVisible(path: string): boolean {
  return pathVisible(path, fresh() ? resolved.value?.visibleModules ?? [] : [])
}
export function resetSellerEntryVisibility(): void {
  ++generation
  if (expiry) clearTimeout(expiry)
  expiry = null; owner = ''; resolved.value = null; sellerEntryState.value = 'idle'
}
/** Memory-only, actor-owned resolution; visibility never changes routes, drafts or server permissions. */
export async function loadSellerEntryVisibility(): Promise<void> {
  resetSellerEntryVisibility()
  const key = ownerNow(), run = generation
  if (!key) return
  owner = key; sellerEntryState.value = 'loading'
  try {
    const value = await getResolvedSellerEntries()
    if (run !== generation || key !== ownerNow()) return
    const remaining = Date.parse(value.expiresAt) - Date.now()
    if (!Number.isFinite(remaining) || remaining <= 0 || remaining > value.maxStaleSeconds * 1000 + 1000) throw new Error('入口配置已过期')
    resolved.value = value; sellerEntryState.value = 'ready'
    expiry = setTimeout(() => { if (run === generation && key === ownerNow()) void loadSellerEntryVisibility() }, remaining)
  } catch {
    if (run === generation && key === ownerNow()) { resolved.value = null; sellerEntryState.value = 'error' }
  }
}
